/**
 * Logger utility with configurable verbosity levels
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

class Logger {
  private level: LogLevel = LogLevel.INFO;

  setLevel(level: LogLevel) {
    this.level = level;
  }

  getLevel(): LogLevel {
    return this.level;
  }

  error(...args: any[]) {
    if (this.level >= LogLevel.ERROR) {
      console.error(...args);
    }
  }

  warn(...args: any[]) {
    if (this.level >= LogLevel.WARN) {
      console.warn(...args);
    }
  }

  info(...args: any[]) {
    if (this.level >= LogLevel.INFO) {
      console.info(...args);
    }
  }

  debug(...args: any[]) {
    if (this.level >= LogLevel.DEBUG) {
      console.log(...args);
    }
  }

  // Convenience method to log with a specific level
  log(level: LogLevel, ...args: any[]) {
    switch (level) {
      case LogLevel.ERROR:
        this.error(...args);
        break;
      case LogLevel.WARN:
        this.warn(...args);
        break;
      case LogLevel.INFO:
        this.info(...args);
        break;
      case LogLevel.DEBUG:
        this.debug(...args);
        break;
    }
  }
}

// Create singleton instance
export const logger = new Logger();

// Make logger available on window for easy runtime configuration
if (typeof window !== 'undefined') {
  (window as any).logger = logger;
  (window as any).LogLevel = LogLevel;
  
  // Add convenience method to window
  (window as any).setLogLevel = (level: keyof typeof LogLevel | LogLevel) => {
    if (typeof level === 'string') {
      logger.setLevel(LogLevel[level]);
      console.info(`Log level set to ${level}`);
    } else {
      logger.setLevel(level);
      console.info(`Log level set to ${LogLevel[level]}`);
    }
  };
  
  // Log initial setup
  console.info('Logger initialized. Use window.setLogLevel("DEBUG") to enable debug logging, or window.setLogLevel("WARN") to reduce verbosity.');
}

export default logger;